import { useState, useEffect } from 'react';
import { Accessibility, X, Type, ZoomIn, ZoomOut, Contrast, MousePointer2, Underline, RotateCcw } from 'lucide-react';

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    highlightLinks: false,
    bigCursor: false,
  });

  useEffect(() => {
    // Apply font size
    document.documentElement.style.fontSize = `${settings.fontSize}%`;
    
    // Apply high contrast
    if (settings.contrast === 'high') {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    // Apply highlight links
    if (settings.highlightLinks) {
      document.body.classList.add('highlight-links');
    } else {
      document.body.classList.remove('highlight-links');
    }
    
    // Apply big cursor
    if (settings.bigCursor) {
      document.body.classList.add('big-cursor');
    } else {
      document.body.classList.remove('big-cursor');
    }
  }, [settings]);

  const increaseFontSize = () => {
    setSettings(prev => ({ ...prev, fontSize: Math.min(prev.fontSize + 10, 150) }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({ ...prev, fontSize: Math.max(prev.fontSize - 10, 80) }));
  };

  const toggleContrast = () => {
    setSettings(prev => ({ 
      ...prev, 
      contrast: prev.contrast === 'normal' ? 'high' : 'normal' 
    }));
  };

  const toggleHighlightLinks = () => {
    setSettings(prev => ({ ...prev, highlightLinks: !prev.highlightLinks }));
  };

  const toggleBigCursor = () => {
    setSettings(prev => ({ ...prev, bigCursor: !prev.bigCursor }));
  };

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
    });
  };

  return (
    <>
      {/* Accessibility Styles */}
      <style>{`
        .high-contrast {
          filter: contrast(1.25);
        }
        .highlight-links a {
          background-color: yellow !important;
          color: black !important;
          text-decoration: underline !important;
        }
        .big-cursor, .big-cursor * {
          cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='black' stroke='white' stroke-width='1'%3E%3Cpath d='M4 4l16 8-8 2-2 8z'/%3E%3C/svg%3E"), auto !important;
        }
      `}</style>

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-5 left-5 z-50 w-14 h-14 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        aria-label="Accessibility Options"
        data-testid="accessibility-widget-toggle"
      >
        {isOpen ? (
          <X className="w-7 h-7" />
        ) : (
          <Accessibility className="w-7 h-7" />
        )}
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 left-5 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 w-72"
          role="dialog"
          aria-label="Accessibility Settings"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Accessibility className="w-5 h-5 text-[#2563eb]" />
            Accessibility Options
          </h3>

          <div className="space-y-4">
            {/* Font Size */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Font Size ({settings.fontSize}%)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={decreaseFontSize}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                  aria-label="Decrease font size"
                >
                  <ZoomOut className="w-4 h-4" />
                  A-
                </button>
                <button
                  onClick={increaseFontSize}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                  aria-label="Increase font size"
                >
                  <ZoomIn className="w-4 h-4" />
                  A+
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button
              onClick={toggleContrast}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                settings.contrast === 'high' 
                  ? 'bg-[#2563eb] text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-pressed={settings.contrast === 'high'}
            >
              <Contrast className="w-4 h-4" />
              High Contrast
            </button>

            {/* Highlight Links */}
            <button
              onClick={toggleHighlightLinks}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                settings.highlightLinks 
                  ? 'bg-[#2563eb] text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-pressed={settings.highlightLinks}
            >
              <Underline className="w-4 h-4" />
              Highlight Links
            </button>

            {/* Big Cursor */}
            <button
              onClick={toggleBigCursor}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                settings.bigCursor 
                  ? 'bg-[#2563eb] text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-pressed={settings.bigCursor}
            >
              <MousePointer2 className="w-4 h-4" />
              Big Cursor
            </button>

            {/* Reset */}
            <button
              onClick={resetSettings}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Settings
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            <a href="/website-accessibility" className="text-[#2563eb] hover:underline">
              View Accessibility Statement
            </a>
          </p>
        </div>
      )}
    </>
  );
};
